/**
 * Fargepaletter til utprøving.
 *
 * Utgangspunktet er at DN-systemet ALLEREDE er paletten; oppgaven er å gi
 * hver del av kartet riktig rolle i den. Hierarkiet som går igjen:
 *
 *   bakgrunn  → lys      (land, vann, park)
 *   geografi  → medium   (skog, bygg, veier)
 *   historie  → mettet   (gul bil, grønn buss)
 *
 * Gul og grønn skal være de eneste virkelig mettede fargene i scenen. Jo
 * mer monokromt kartet er, desto hardere treffer de to rutene.
 *
 * Stilen slår opp 25 palettnøkler, men en palett beskrives med ~14 valg.
 * `fromTokens()` utleder resten, så hver palett under er til å lese.
 *
 * ADAPTIV CASING er viktigere enn å finne den perfekte grønnfargen: gul har
 * høy luminans og trenger mørk kant, grønn er mørk og trenger lys kant.
 * Derfor bærer hver palett sine egne casing-farger, ikke bare linjefarger.
 */

/** Blander to hex-farger. t=0 gir a, t=1 gir b. */
function mix(a, b, t) {
  const p = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [ra, ga, ba] = p(a);
  const [rb, gb, bb] = p(b);
  const c = (x, y) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${c(ra, rb)}${c(ga, gb)}${c(ba, bb)}`;
}

/**
 * Utleder en full PALETTE fra kompakte tokens.
 *
 *   land, water, waterDeep (Oslofjorden), forest, park,
 *   building, roadMinor, roadPrimary, roadMajor, roadCasing,
 *   label, labelSecondary
 *
 * Alt kan overstyres eksplisitt via `extra`.
 */
function fromTokens(t, extra = {}) {
  return {
    background: t.background ?? t.land,
    water: t.water,
    // Oslofjorden får være litt dypere enn elver og småvann, men ikke så mørk
    // at den blir hovedperson – den skal hjelpe deg å kjenne igjen Oslo.
    waterDeep: t.waterDeep ?? mix(t.water, '#000000', 0.08),
    forest: t.forest,
    grass: t.park,
    landuse: mix(t.land, t.building, 0.25),
    pier: t.pier ?? mix(t.land, t.building, 0.5),
    plaza: mix(t.land, t.roadMajor, 0.5),

    building: t.building,
    buildingTop: t.buildingTop ?? mix(t.building, '#ffffff', 0.35),

    roadPath: mix(t.land, t.roadMinor, 0.6),
    roadService: t.roadMinor,
    roadResidential: t.roadMinor,
    roadResidentialCasing: t.roadCasing,
    roadTertiary: mix(t.roadMinor, t.roadPrimary ?? t.roadMajor, 0.4),
    roadSecondary: mix(t.roadMinor, t.roadPrimary ?? t.roadMajor, 0.7),
    roadSecondaryCasing: t.roadCasing,
    roadPrimary: t.roadPrimary ?? mix(t.roadMinor, t.roadMajor, 0.8),
    roadPrimaryCasing: t.roadCasing,
    roadMajor: t.roadMajor,
    roadMajorCasing: t.roadCasing,

    rail: mix(t.roadCasing, t.label, 0.2),

    labelText: t.label,
    labelHalo: t.halo ?? t.background ?? t.land,
    labelWater: t.labelSecondary,
    ...extra,
  };
}

// --- Rutefarger ---------------------------------------------------------
// Objekt og rute deler verdi, så «gul linje → gul bil» leses uten legend.
const LAMBO = '#ffcc33';
const BUS_DAY = '#34634a';
// Lysere buss på mørkt kart – #34634a drukner mot mørk blå (2,2:1).
// #aac8ba er vakrere mot navy, men lar seg ikke skille fra den gule bilen
// (1,2:1 i luminans, bare hue skiller dem – svikter ved fargeblindhet).
// #6e9783 er kompromisset: 4,6:1 mot land, 2,2:1 mot bilen.
const BUS_DARK = '#6e9783';

/** Rutefarger med kant tilpasset bakgrunnens lyshet. */
function routes({ bus = BUS_DAY, busCasing = '#ffffff', car = LAMBO, carCasing = '#232528' } = {}) {
  return {
    bus: { line: bus, casing: busCasing, casingOpacity: 0.9 },
    car: { line: car, casing: carCasing, casingOpacity: 0.8 },
  };
}

// --- Lys ----------------------------------------------------------------
const NEUTRAL_LIGHT = { anchor: 'map', position: [1.15, 120, 40], color: '#ffffff', intensity: 0.25 };
const MORNING_LIGHT = { anchor: 'map', position: [1.15, 90, 55], color: '#fff3e4', intensity: 0.3 };
/** Arkitekturmodell: formen kommer nesten utelukkende fra lyset. */
const MODEL_LIGHT = { anchor: 'map', position: [1.5, 315, 45], color: '#ffffff', intensity: 0.32 };
const NIGHT_LIGHT = { anchor: 'map', position: [1.15, 250, 60], color: '#9fb6e0', intensity: 0.5 };

const SKY_BASE = {
  'sky-horizon-blend': 0.6, 'horizon-fog-blend': 0.6, 'fog-ground-blend': 0.6,
  'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 9, 0.6, 16, 0.3],
};
const SKY_LIGHT = { ...SKY_BASE, 'sky-color': '#cfe0e8', 'horizon-color': '#f0e6d6', 'fog-color': '#eef2f8' };
const SKY_COOL = { ...SKY_BASE, 'sky-color': '#cfe0e8', 'horizon-color': '#dfe8f5', 'fog-color': '#e6edf8' };
const SKY_WHITE = { ...SKY_BASE, 'sky-color': '#eef0f3', 'horizon-color': '#ffffff', 'fog-color': '#f7f8fa' };
const SKY_DARK = { ...SKY_BASE, 'sky-color': '#01081c', 'horizon-color': '#0a1b3d', 'fog-color': '#071132' };

/**
 * De valgbare palettene. `hotkey` velger paletten direkte fra tastaturet,
 * med uendret kamera – da ser man forskjellen på sekunder i stedet for å
 * måtte finne tilbake til samme utsnitt for hver variant.
 */
export const PALETTES = {
  // --- DN-familien ------------------------------------------------------
  editorial: {
    label: 'DN Editorial', hotkey: '1',
    palette: fromTokens({
      land: '#f4f4f6', water: '#d8e2f2', waterDeep: '#a6b8d8',
      forest: '#d0e9da', park: '#edf5f0',
      building: '#d1d3d6', buildingTop: '#e4e5e7',
      roadMinor: '#e4e5e7', roadPrimary: '#f4f4f6', roadMajor: '#ffffff',
      roadCasing: '#c1c3c8', label: '#363a3f', labelSecondary: '#6b717a',
    }),
    routes: routes(), light: NEUTRAL_LIGHT, sky: SKY_LIGHT,
  },

  blue: {
    label: 'DN Blue', hotkey: '2',
    palette: fromTokens({
      land: '#edf1f8', water: '#a6b8d8', waterDeep: '#8ba3cb',
      forest: '#d0e9da', park: '#edf5f0',
      building: '#d1d3d6', roadMinor: '#e4e5e7', roadMajor: '#ffffff',
      roadCasing: '#a0a5ac', label: '#13264a', labelSecondary: '#53555f',
    }),
    routes: routes(), light: NEUTRAL_LIGHT, sky: SKY_COOL,
  },

  architectural: {
    label: 'DN Architectural', hotkey: '3',
    palette: fromTokens({
      land: '#e4e5e7', water: '#d8e2f2', forest: '#d0e9da', park: '#e9eef0',
      // Bil og buss blir nærmest de eneste mettede fargene i hele scenen.
      building: '#c1c3c8', buildingTop: '#d6d8db',
      roadMinor: '#d1d3d6', roadPrimary: '#f4f4f6', roadMajor: '#ffffff',
      roadCasing: '#a0a5ac', label: '#363a3f', labelSecondary: '#6b717a',
    }),
    routes: routes(), light: MODEL_LIGHT, sky: SKY_LIGHT,
  },

  green: {
    label: 'DN Green', hotkey: '4',
    palette: fromTokens({
      land: '#edf5f0', water: '#d8e2f2', forest: '#aac8ba', park: '#d0e9da',
      building: '#d1d3d6', roadMinor: '#d1d3d6', roadMajor: '#ffffff',
      roadCasing: '#b3bfb8', label: '#14412f', labelSecondary: '#53555f',
    }),
    // Skogen er #aac8ba, så bussen går helt mørk: enorm kontrast innenfor
    // samme fargeskala, uten å innføre en ny hue.
    routes: routes({ bus: '#14412f' }), light: MORNING_LIGHT, sky: SKY_LIGHT,
  },

  bluehour: {
    label: 'DN Blue Hour', hotkey: '5',
    palette: fromTokens({
      background: '#010e29', land: '#13264a', water: '#000716',
      forest: '#14412f', park: '#214e3d',
      building: '#283c62', buildingTop: '#35507f',
      roadMinor: '#363a3f', roadMajor: '#53555f',
      roadCasing: '#1d2c48', label: '#edf1f8', labelSecondary: '#a0a5ac',
      halo: '#010e29',
    }),
    // Gul mot #13264a er hele poenget her. Bussen må lysne for å overleve.
    routes: routes({ bus: BUS_DARK, busCasing: '#0a1832' }),
    light: NIGHT_LIGHT, sky: SKY_DARK,
  },

  // --- Monokrome: all fargekapasitet reservert til de to rutene ----------
  neutral: {
    label: 'DN Neutral (mono)', hotkey: '6',
    palette: fromTokens({
      land: '#f4f4f6',
      // Ingen grønn skog og ingen blått vann. Skog, vann og by skilles på
      // luminans, geometri og lys – ikke på hue. Terrenget gir form via
      // 3D-pitch, byggene via extrusion, veiene via linjer.
      water: '#e4e5e7', forest: '#e4e5e7', park: '#eeeeef',
      building: '#d1d3d6', roadMinor: '#d1d3d6', roadPrimary: '#e4e5e7',
      roadMajor: '#ffffff', roadCasing: '#c1c3c8',
      label: '#53555f', labelSecondary: '#858b94',
    }),
    routes: routes(), light: NEUTRAL_LIGHT, sky: SKY_LIGHT,
  },

  whitemodel: {
    label: 'DN White Model (mono)', hotkey: '7',
    palette: fromTokens({
      background: '#ffffff', land: '#f4f4f6',
      water: '#e4e5e7', forest: '#eeeeef', park: '#f4f4f6',
      building: '#e4e5e7', buildingTop: '#f2f2f3',
      roadMinor: '#f4f4f6', roadPrimary: '#fafafb', roadMajor: '#ffffff',
      roadCasing: '#d1d3d6', label: '#53555f', labelSecondary: '#858b94',
    }),
    routes: routes(), light: MODEL_LIGHT, sky: SKY_WHITE,
  },

  bluemono: {
    label: 'DN Blue Mono', hotkey: '8',
    palette: fromTokens({
      land: '#edf1f8', water: '#d8e2f2', forest: '#e3e9f2', park: '#e8edf5',
      building: '#d8e2f2', buildingTop: '#e6ecf6',
      roadMinor: '#dfe6f0', roadPrimary: '#d8e2f2', roadMajor: '#ffffff',
      roadCasing: '#bcc8dc', label: '#283c62', labelSecondary: '#6b7f9e',
    }, { labelWater: '#13264a' }),
    routes: routes(), light: NEUTRAL_LIGHT, sky: SKY_COOL,
  },

  darknavy: {
    label: 'DN Dark Navy (mono)', hotkey: '9',
    palette: fromTokens({
      background: '#000716', land: '#13264a', water: '#010e29',
      forest: '#182d50', park: '#1d3459',
      building: '#283c62', buildingTop: '#35507f',
      roadMinor: '#363a3f', roadMajor: '#53555f',
      roadCasing: '#1d2c48', label: '#edf1f8', labelSecondary: '#a0a5ac',
      halo: '#000716',
    }),
    routes: routes({ bus: BUS_DARK, busCasing: '#071229' }),
    light: NIGHT_LIGHT, sky: SKY_DARK,
  },

  warmgrey: {
    label: 'Warm Grey (mono)',
    palette: fromTokens({
      land: '#eeede9', water: '#deded9', forest: '#deddd7', park: '#e8e7e2',
      building: '#d0cfca', roadMinor: '#d3d2cd', roadPrimary: '#e8e7e2',
      roadMajor: '#faf9f6', roadCasing: '#bfbeb8',
      label: '#53555f', labelSecondary: '#858b94',
    }),
    routes: routes(), light: MORNING_LIGHT, sky: SKY_LIGHT,
  },

  varm: {
    label: 'Varm beige (original)',
    palette: {
      background: '#cfc7b6', water: '#8ea3ad', waterDeep: '#7f96a1',
      forest: '#5f7a55', grass: '#8ea07f', landuse: '#c8c1af',
      pier: '#c4a878', plaza: '#d6cdba',
      building: '#c3b8a2', buildingTop: '#e0d4ba',
      roadPath: '#c2b8a5', roadService: '#cabfad',
      roadResidential: '#ded7c8', roadResidentialCasing: '#bdb4a1',
      roadTertiary: '#dcd3c2', roadSecondary: '#d3c9b4', roadSecondaryCasing: '#b3a891',
      roadPrimary: '#d0c4ac', roadPrimaryCasing: '#a99c84',
      roadMajor: '#a2957d', roadMajorCasing: '#897c65',
      rail: '#a39b8c',
      labelText: '#3a362f', labelHalo: '#d9d2c2', labelWater: '#4f5e66',
    },
    routes: routes({ bus: '#3fae2a', busCasing: '#1f4a15', car: '#FFE500', carCasing: '#4a4300' }),
    light: { anchor: 'map', position: [1.15, 90, 80], color: '#ffe6c2', intensity: 0.45 },
    sky: SKY_LIGHT,
  },
};

export const DEFAULT_PALETTE = 'editorial';

/** hotkey → palettnøkkel, for tastatursnarveiene. */
export const HOTKEYS = Object.fromEntries(
  Object.entries(PALETTES).filter(([, t]) => t.hotkey).map(([key, t]) => [t.hotkey, key]),
);
